import { useState, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Sidebar } from "primereact/sidebar";
import { examReportService } from "../../service/ExamReport/examReport.service";
import { IExamReport } from "./ExamReport.model";
import { DIFFICULTY_LEVELS } from "../../library/utilities/constants/constant";

const ExamReport = () => {
  const [data, setData] = useState<IExamReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);

  // Filter states
  const [topicFilter, setTopicFilter] = useState<string>("");
  const [candidateFilter, setCandidateFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  const [percentageFilter, setPercentageFilter] = useState<string>("");
  const [writingAccuracyFilter, setWritingAccuracyFilter] =
    useState<string>("");
  const [yearOfExperienceFilter, setYearOfExperienceFilter] =
    useState<string>("");

  const percentageRangeOptions = [
    { label: "All", value: "" },
    { label: "0-25%", value: "0-25" },
    { label: "26-50%", value: "26-50" },
    { label: "51-75%", value: "51-75" },
    { label: "76-100%", value: "76-100" },
  ];

  const writingAccuracyOptions = [
    { label: "All", value: "" },
    { label: "0-25%", value: "0-25" },
    { label: "26-50%", value: "26-50" },
    { label: "51-75%", value: "51-75" },
    { label: "76-100%", value: "76-100" },
  ];

  const yearOfExperienceOptions = [
    { label: "All", value: "" },
    { label: "0-2 years", value: "0-2" },
    { label: "3-5 years", value: "3-5" },
    { label: "6-10 years", value: "6-10" },
    { label: "10+ years", value: "10+" },
  ];

  useEffect(() => {
    loadExamReports();
  }, []);

  const loadExamReports = async () => {
    setLoading(true);
    try {
      const response = await examReportService.getAll();
      if (response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Failed to load exam reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const calculateExamStats = (exam: IExamReport) => {
    const interObj = (exam as any).interview_question_object;

    if (!interObj || !interObj.interviewQuestions) {
      return {
        totalQuestions: 0,
        attempted: 0,
        notAttempted: 0,
        accuracy: 0,
        high: { correct: 0, total: 0 },
        medium: { correct: 0, total: 0 },
        low: { correct: 0, total: 0 },
      };
    }

    const questions = interObj.interviewQuestions || [];

    const summary = {
      high: { correct: 0, total: 0 },
      medium: { correct: 0, total: 0 },
      low: { correct: 0, total: 0 },
    };
    let attempted = 0;
    let notAttempted = 0;
    let correctAnswers = 0;

    questions.forEach((que: any) => {
      if (que.difficulty_level === DIFFICULTY_LEVELS.HIGH) {
        summary.high.total++;
        if (que.is_correct) {
          summary.high.correct++;
          correctAnswers++;
        }
      }
      if (que.difficulty_level === DIFFICULTY_LEVELS.MEDIUM) {
        summary.medium.total++;
        if (que.is_correct) {
          summary.medium.correct++;
          correctAnswers++;
        }
      }
      if (que.difficulty_level === DIFFICULTY_LEVELS.LOW) {
        summary.low.total++;
        if (que.is_correct) {
          summary.low.correct++;
          correctAnswers++;
        }
      }
      if (que.attempted) {
        attempted++;
      } else {
        notAttempted++;
      }
    });

    const accuracy = interObj?.writing_test
      ? interObj.writing_test?.[0]?.accuracy || 0
      : 0;

    return {
      totalQuestions: questions.length,
      attempted,
      notAttempted,
      accuracy,
      high: summary.high,
      medium: summary.medium,
      low: summary.low,
    };
  };

  const enhancedData = useMemo(() => {
    return data.map((exam) => ({
      ...exam,
      stats: calculateExamStats(exam),
    }));
  }, [data]);

  const filteredData = useMemo(() => {
    return enhancedData.filter((exam: IExamReport) => {
      // Candidate filter (name)
      if (candidateFilter) {
        const search = candidateFilter.toLowerCase();
        const candidateMatch = exam.candidate_name
          ?.toLowerCase()
          .includes(search);
        if (!candidateMatch) return false;
      }

      // Topic filter
      if (
        topicFilter &&
        !exam.candidate_name?.toLowerCase().includes(topicFilter.toLowerCase())
      ) {
        return false;
      }

      // Date range filter
      if (dateRange?.[0] || dateRange?.[1]) {
        if (!exam.submit_exam_date) return false;
        const examDate = new Date(exam.submit_exam_date);
        if (isNaN(examDate.getTime())) return false;

        if (dateRange[0]) {
          const start = new Date(dateRange[0]);
          start.setHours(0, 0, 0, 0);
          if (examDate < start) return false;
        }
        if (dateRange[1]) {
          const end = new Date(dateRange[1]);
          end.setHours(23, 59, 59, 999);
          if (examDate > end) return false;
        }
      }

      // Percentage filter
      if (percentageFilter) {
        const [min, max] = percentageFilter.split("-").map(Number);
        if (exam.percentage < min || exam.percentage > max) return false;
      }

      // Writing accuracy filter
      if (writingAccuracyFilter) {
        const [min, max] = writingAccuracyFilter.split("-").map(Number);
        const accuracy = exam.stats?.accuracy || 0;
        if (accuracy < min || accuracy > max) return false;
      }

      // Year of experience filter
      if (yearOfExperienceFilter) {
        const experience = (exam as IExamReport).year_of_experience || 0;
        const ranges: Record<string, [number, number]> = {
          "0-2": [0, 2],
          "3-5": [3, 5],
          "6-10": [6, 10],
          "10+": [11, Infinity],
        };
        const [min, max] = ranges[yearOfExperienceFilter] || [0, Infinity];
        if (experience < min || experience > max) return false;
      }

      return true;
    });
  }, [
    enhancedData,
    candidateFilter,
    topicFilter,
    dateRange,
    percentageFilter,
    writingAccuracyFilter,
    yearOfExperienceFilter,
  ]);

  const percentageBodyTemplate = (row: IExamReport) => {
    const severity =
      row.percentage >= 75
        ? "success"
        : row.percentage >= 50
          ? "info"
          : row.percentage >= 25
            ? "warning"
            : "danger";
    return <Tag value={`${row.percentage}%`} severity={severity} />;
  };

  const questionStatsTemplate = (correct: number, total: number) => (
    <span className="font-semibold">
      <span className="text-green-600">{correct}</span>
      <span className="text-gray-500"> / </span>
      <span className="text-blue-700">{total} total</span>
    </span>
  );

  const clearAllFilters = () => {
    setTopicFilter("");
    setCandidateFilter("");
    setDateRange(null);
    setPercentageFilter("");
    setWritingAccuracyFilter("");
    setYearOfExperienceFilter("");
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (topicFilter) count++;
    if (candidateFilter) count++;
    if (dateRange && dateRange.length === 2) count++;
    if (percentageFilter) count++;
    if (writingAccuracyFilter) count++;
    if (yearOfExperienceFilter) count++;
    return count;
  };

  const columns = [
    { field: "candidate_name", header: "Candidate Name" },
    { field: "year_of_experience", header: "Year of Experience" },

    {
      field: "start_exam_date",
      header: "Start Date",
      body: (row: IExamReport) => formatDate(row.start_exam_date),
    },
    {
      field: "submit_exam_date",
      header: "Submit Date",
      body: (row: IExamReport) => formatDate(row.submit_exam_date),
    },
    {
      field: "total_questions",
      header: "Total Questions",
      body: (row: IExamReport) => row.stats.totalQuestions,
    },
    {
      field: "attempted",
      header: "Attempted",
      body: (row: IExamReport) => row.stats.attempted,
    },
    {
      field: "not_attempted",
      header: "Not Attempted",
      body: (row: IExamReport) => row.stats.notAttempted,
    },
    {
      field: "accuracy",
      header: "Accuracy of Writing test",
      body: (row: IExamReport) => `${row.stats.accuracy}%`,
    },
    {
      field: "high_questions",
      header: "High Questions",
      body: (row: IExamReport) =>
        questionStatsTemplate(row.stats.high.correct, row.stats.high.total),
    },
    {
      field: "medium_questions",
      header: "Medium Questions",
      body: (row: IExamReport) =>
        questionStatsTemplate(row.stats.medium.correct, row.stats.medium.total),
    },
    {
      field: "low_questions",
      header: "Low Questions",
      body: (row: IExamReport) =>
        questionStatsTemplate(row.stats.low.correct, row.stats.low.total),
    },
    {
      field: "percentage",
      header: "Percentage",
      body: percentageBodyTemplate,
    },
  ];

  return (
    <div className="p-4">
      <Card>
        <div className="flex justify-content-between align-items-center mb-4">
          <h2 className="m-0">Exam Reports</h2>
          <Button
            icon="pi pi-filter"
            label={`Filters ${getActiveFilterCount() > 0 ? `(${getActiveFilterCount()})` : ""}`}
            onClick={() => setFilterVisible(true)}
            severity="secondary"
            outlined
          />
        </div>

        <Sidebar
          visible={filterVisible}
          position="right"
          onHide={() => setFilterVisible(false)}
          style={{ width: "400px" }}
          header="Filter Options">
          <div className="flex flex-column gap-4">
            {/* Topic Filter */}
            <div className="flex flex-column gap-2">
              <label className="text-sm font-medium">Topic</label>
              <InputText
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                placeholder="Filter by topic"
                className="w-full"
              />
            </div>

            {/* Candidate Filter */}
            <div className="flex flex-column gap-2">
              <label className="text-sm font-medium">Candidate</label>
              <InputText
                value={candidateFilter}
                onChange={(e) => setCandidateFilter(e.target.value)}
                placeholder="Search by name or email"
                className="w-full"
              />
            </div>

            {/* Date Range Filter */}
            <div className="flex flex-column gap-2">
              <label className="text-sm font-medium">
                Date Range (Start - End)
              </label>
              <Calendar
                value={dateRange}
                onChange={(e) => setDateRange(e.value as Date[] | null)}
                selectionMode="range"
                placeholder="Select date range"
                dateFormat="yy-mm-dd"
                showIcon
                showButtonBar
                readOnlyInput
                className="w-full"
              />
            </div>

            {/* Percentage Range Filter */}
            <div className="flex flex-column gap-2">
              <label className="text-sm font-medium">Percentage Range</label>
              <Dropdown
                value={percentageFilter}
                options={percentageRangeOptions}
                onChange={(e) => setPercentageFilter(e.value)}
                placeholder="Select percentage range"
                className="w-full"
              />
            </div>

            {/* Writing Accuracy Range Filter */}
            <div className="flex flex-column gap-2">
              <label className="text-sm font-medium">
                Writing Accuracy Range
              </label>
              <Dropdown
                value={writingAccuracyFilter}
                options={writingAccuracyOptions}
                onChange={(e) => setWritingAccuracyFilter(e.value)}
                placeholder="Select accuracy range"
                className="w-full"
              />
            </div>

            {/* Year of Experience Filter */}
            <div className="flex flex-column gap-2">
              <label className="text-sm font-medium">Year of Experience</label>
              <Dropdown
                value={yearOfExperienceFilter}
                options={yearOfExperienceOptions}
                onChange={(e) => setYearOfExperienceFilter(e.value)}
                placeholder="Select experience range"
                className="w-full"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-3">
              <Button
                icon="pi pi-filter-slash"
                label="Clear All"
                severity="secondary"
                outlined
                onClick={clearAllFilters}
                className="flex-1"
              />
              <Button
                icon="pi pi-check"
                label="Apply"
                onClick={() => setFilterVisible(false)}
                className="flex-1"
              />
            </div>
          </div>
        </Sidebar>

        {/* Data Table */}
        <DataTable
          value={filteredData}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          dataKey="id"
          emptyMessage="No exam reports found">
          {columns.map((col) => (
            <Column
              sortable
              key={col.field}
              field={col.field}
              header={col.header}
              body={col?.body}
            />
          ))}
        </DataTable>
      </Card>
    </div>
  );
};

export default ExamReport;
